/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file driverPushBus.js
 * @description
 * Tiny in-memory pub/sub between the push bootstrap and the driver screens.
 *
 *  - **Push handlers** — a foreground driver push is offered to the registered
 *    handlers, newest first. A handler returns `true` when it consumed the push
 *    (e.g. the focused route screen shows its own "Mi változott?" banner), so the
 *    global banner host (registered first, always consumes) is the fallback.
 *    `emitDriverPush` returns `false` when nobody is registered — i.e. the app is
 *    not in driver mode — so the caller can use the normal local notification.
 *  - **Refresh signal** — every mounted `useDriverResource` re-fetches silently,
 *    so lists are current the instant a push arrives.
 *
 * @module helpers/driverPushBus
 */

const pushHandlers = [];
const refreshListeners = new Set();
const pushObservers = new Set();

/**
 * Register a foreground-push handler (newest handler is asked first).
 * @param {(payload: Object) => boolean} handler Return true when the push was consumed.
 * @returns {() => void} Unsubscribe.
 */
export const registerDriverPushHandler = (handler) => {
    pushHandlers.push(handler);

    return () => {
        const index = pushHandlers.indexOf(handler);

        if (index >= 0) {
            pushHandlers.splice(index, 1);
        }
    };
};

/**
 * Observe every foreground driver push without consuming it (side effects only —
 * e.g. stopping GPS tracking on a route cancel / unassign push).
 * @param {(payload: Object) => void} observer Called for each push.
 * @returns {() => void} Unsubscribe.
 */
export const observeDriverPush = (observer) => {
    pushObservers.add(observer);

    return () => pushObservers.delete(observer);
};

/**
 * Offer a foreground driver push to the handlers.
 * @param {Object} payload Result of `buildDriverPushPayload`.
 * @returns {boolean} True when some handler consumed it.
 */
export const emitDriverPush = (payload) => {
    pushObservers.forEach((observer) => {
        try {
            observer(payload);
        } catch (_error) {
            // An observer must never affect push delivery.
        }
    });

    for (let i = pushHandlers.length - 1; i >= 0; i -= 1) {
        try {
            if (pushHandlers[i](payload) === true) {
                return true;
            }
        } catch (_error) {
            // A faulty screen handler must never swallow a push meant for a driver.
        }
    }

    return false;
};

/**
 * Subscribe to the "data is stale" signal.
 * @param {() => void} listener Called on every refresh request.
 * @returns {() => void} Unsubscribe.
 */
export const subscribeDriverRefresh = (listener) => {
    refreshListeners.add(listener);

    return () => refreshListeners.delete(listener);
};

/**
 * Ask every mounted driver resource to silently re-fetch.
 * @returns {void}
 */
export const emitDriverRefresh = () => {
    refreshListeners.forEach((listener) => {
        try {
            listener();
        } catch (_error) {
            // Isolated: one failing screen must not block the others.
        }
    });
};
